<script setup lang="ts">
export interface SaferGamblingQaContentProps {
    videoId?: string
    videoSrc?: string
    videoPoster?: string
    videoTitle?: string
    /** Off while the Q&A is closed or off-screen, so its media is not loaded or kept playing. */
    showVideo?: boolean
    body: any
    ctas?: Array<{
        label: string
        url: string
        variant?: 'primary' | 'secondary'
    }>
    expandableTextBlocks?: Array<{
        title: string
        content: any
    }>
}

const props = withDefaults(defineProps<SaferGamblingQaContentProps>(), {
    showVideo: true,
    ctas: () => [],
    expandableTextBlocks: () => []
})

const { mediaVideoUrl } = useGameAssets()

const videoUrl = computed(() => mediaVideoUrl(props.videoSrc))

const img = useImage()
const posterUrl = computed(() => props.videoPoster
    ? img(props.videoPoster, { width: 1280, quality: 50 }, { provider: 'cloudflare' })
    : undefined
)

const youtubeEmbedUrl = computed(() => props.videoId ? `https://www.youtube.com/embed/${props.videoId}` : undefined)
</script>

<template>
    <div class="qa-content">
        <div
            v-if="props.showVideo && (videoUrl || youtubeEmbedUrl)"
            class="qa-content__video"
        >
            <video
                v-if="videoUrl"
                :src="videoUrl"
                :poster="posterUrl"
                controls
                playsinline
                preload="metadata"
                :title="props.videoTitle || 'Video'"
            />
            <iframe
                v-else
                :src="youtubeEmbedUrl"
                title="Video"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
            />
        </div>

        <ContentRenderer :value="props.body" />

        <SaferGamblingSubPanel
            v-for="block in props.expandableTextBlocks"
            :key="`expandable-block-${block.title}`"
            :title="block.title"
        >
            <p>{{ block.content }}</p>
        </SaferGamblingSubPanel>

        <UIButton
            v-for="(cta, index) in props.ctas"
            :key="`cta-${index}`"
            :class="['btn btn--block', `btn--${cta.variant || 'primary'}`]"
            :rounded="false"
            :href="cta.url"
            target="_blank"
        >
            {{ cta.label }}
        </UIButton>
    </div>
</template>

<style scoped lang="scss">
.qa-content {
    text-align: left;

    &__video {
        margin-bottom: 20px;

        iframe,
        video {
            width: 100%;
            aspect-ratio: 16 / 9;
            border-radius: var(--safer-gambling-panel-border-radius, $safer-gambling-panel-border-radius);
        }

        video {
            display: block;
            background: #000;
        }
    }

    :deep(p) {
        margin-bottom: 1em;

        > b {
            display: block;
            transform: translateY(10px);
        }
    }

    :deep(ul),
    :deep(ol) {
        padding-left: 1.5em;
        margin-bottom: 1em;

        li {
            text-align: left;
        }
    }

    :deep(h2),
    :deep(h3),
    :deep(h4) {
        margin-top: 1.5em;
        margin-bottom: 0.5em;
        font-weight: $text-bold;
    }

    :deep(.btn) {
        height: 50px;
        align-items: center;
        margin-top: 15px;
    }
}
</style>
